import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * ServerLogs.jsx — All-in-one app observability (no chain methods)
 * Auto-detects localhost vs live. Streams server logs via SSE with poll fallback.
 * Also captures CLIENT-side issues: console errors, window.onerror, unhandledrejection,
 * failed fetch/XHR, and long tasks via PerformanceObserver.
 * Tailwind UI with density + dark toggles. Virtualized list for speed.
 * Hotkeys: P pause, C clear, A autoscroll, F find. Mock: ?mock=1
 */

// ---------- Environment detection ----------
function getHost() {
  if (typeof window === "undefined") return "";
  return window.location.hostname || "";
}
function isLocalHost(host) {
  if (!host) return true;
  if (host === "localhost") return true;
  if (host.endsWith(".local")) return true;
  var isIp = /^\d{1,3}(\.\d{1,3}){3}$/.test(host);
  if (isIp) return true;
  return false;
}
function readApiOverride() {
  if (typeof window === "undefined") return null;
  try {
    var params = new URLSearchParams(window.location.search);
    var q = params.get("api"); // ?api=local | live | full URL
    if (q === "local" || q === "live") return q;
    if (q && q.startsWith("http")) return q;
  } catch (_) {}
  return null;
}
function resolveBaseUrl() {
  var override = readApiOverride();
  if (override === "local") return "http://localhost:3000";
  if (override === "live") return "https://www.pawsible.app";
  if (override && override.startsWith("http")) return override;
  var host = getHost();
  return isLocalHost(host) ? "http://localhost:3000" : "https://www.pawsible.app";
}

var BASE_URL = resolveBaseUrl();
var DEFAULT_SSE_URL = BASE_URL + "/api/admin/logs/stream";
var DEFAULT_POLL_URL = BASE_URL + "/api/admin/logs";
var MAX_BUFFER = 5000;

var LEVELS = ["trace", "debug", "info", "warn", "error", "fatal"];
var LEVEL_STYLES = {
  trace: "bg-slate-100 text-slate-700",
  debug: "bg-indigo-100 text-indigo-700",
  info: "bg-emerald-100 text-emerald-700",
  warn: "bg-amber-100 text-amber-800",
  error: "bg-rose-100 text-rose-700",
  fatal: "bg-black text-white",
};

function classNames() {
  var out = [];
  for (var i = 0; i < arguments.length; i++) {
    if (arguments[i]) out.push(arguments[i]);
  }
  return out.join(" ");
}
function useQueryFlag(key) {
  return useMemo(function () {
    if (typeof window === "undefined") return false;
    var u = new URL(window.location.href);
    return u.searchParams.get(key) === "1";
  }, [key]);
}
function nowISO() { return new Date().toISOString(); }
function safeParse(line) {
  try { return JSON.parse(line); }
  catch (e) { return { ts: nowISO(), level: "info", msg: line }; }
}

export default function ServerLogs(props) {
  var sseUrl = props && props.sseUrl ? props.sseUrl : DEFAULT_SSE_URL;
  var pollUrl = props && props.pollUrl ? props.pollUrl : DEFAULT_POLL_URL;
  var pageTitle = props && props.pageTitle ? props.pageTitle : "Server Logs";

  var mockMode = useQueryFlag("mock");

  var [rows, setRows] = useState([]);         // {ts, level, msg, meta, source}
  var [levels, setLevels] = useState(function () { return new Set(LEVELS); });
  var [search, setSearch] = useState("");
  var [paused, setPaused] = useState(false);
  var [autoScroll, setAutoScroll] = useState(true);
  var [prettyJSON, setPrettyJSON] = useState(false);
  var [connected, setConnected] = useState(false);
  var [sinceISO, setSinceISO] = useState(nowISO());
  var [usingPoll, setUsingPoll] = useState(false);
  var [dense, setDense] = useState(true);
  var [dark, setDark] = useState(false);
  var [clientCaptureOn, setClientCaptureOn] = useState(true);

  var listRef = useRef(null);
  var endRef = useRef(null);
  var sseRef = useRef(null);
  var pollTimer = useRef(null);
  var clientTearDownRef = useRef(function(){});

  // ---------- Client observers: console, errors, fetch/XHR, long tasks ----------
  useEffect(function () {
    if (!clientCaptureOn) {
      try { clientTearDownRef.current(); } catch(_){ }
      clientTearDownRef.current = function(){};
      return;
    }
    var teardown = attachClientObservers(function (log) {
      if (paused) return;
      appendRows([normalizeItem(log)]);
    });
    clientTearDownRef.current = teardown;
    return function () { try { teardown(); } catch(_){ } };
  }, [clientCaptureOn, paused]);

  // ---------- SSE connect with poll fallback ----------
  useEffect(function () {
    if (mockMode) return;

    var es = new EventSource(sseUrl, { withCredentials: true });
    sseRef.current = es;

    es.onopen = function () {
      setConnected(true);
      setUsingPoll(false);
    };
    es.onerror = function () {
      setConnected(false);
      try { es.close(); } catch(_){ }
      if (!usingPoll) startPolling();
    };
    es.onmessage = function (ev) {
      if (paused) return;
      var data = safeParse(ev.data);
      appendRows([normalizeItem(data)]);
    };

    return function () {
      try { es.close(); } catch(_){ }
      stopPolling();
    };
  }, [sseUrl, paused]);

  function startPolling() {
    setUsingPoll(true);
    stopPolling();
    var tick = function () { return __pollTick(); };
    tick();
    pollTimer.current = setInterval(tick, 1500);
  }
  async function __pollTick() {
    if (paused) return;
    try {
      var url = new URL(pollUrl, window.location.origin);
      url.searchParams.set("since", sinceISO);
      var res = await fetch(url.toString(), { credentials: "include" });
      if (!res.ok) throw new Error("poll failed");
      var json = await res.json();
      var items = json && json.items ? json.items : [];
      if (items && items.length) {
        var normalized = [];
        var i;
        for (i = 0; i < items.length; i++) {
          normalized.push(normalizeItem(items[i]));
        }
        appendRows(normalized);
        setSinceISO(json.since ? json.since : nowISO());
      }
      setConnected(true);
    } catch (e) {
      setConnected(false);
    }
  }
  function stopPolling() {
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  }

  // ---------- Mock generator ----------
  useEffect(function () {
    if (!mockMode) return;
    setConnected(true);
    var lvls = ["info", "warn", "error", "debug"];
    var msgs = [
      "HTTP 200 GET /api/health",
      "Stripe webhook received",
      "Booking created",
      "Queue drained 12 jobs",
      "DB connection healthy",
      "Unexpected token at line 221",
      "Payment intent confirmed",
      "Membership renewal success",
      "Slow query detected",
    ];
    var t = setInterval(function () {
      if (paused) return;
      var it = normalizeItem({
        ts: nowISO(),
        level: lvls[Math.floor(Math.random() * lvls.length)],
        msg: msgs[Math.floor(Math.random() * msgs.length)],
        meta: { rid: Math.random().toString(36).slice(2, 8) },
        source: "mock",
      });
      appendRows([it]);
    }, 400);
    return function () { clearInterval(t); };
  }, [mockMode, paused]);

  // ---------- Helpers ----------
  function normalizeItem(it) {
    var obj = typeof it === "string" ? safeParse(it) : (it || {});
    var lvl = (obj.level || "info").toLowerCase();
    var okLevel = "info";
    var j;
    for (j = 0; j < LEVELS.length; j++) {
      if (LEVELS[j] === lvl) { okLevel = lvl; break; }
    }
    return {
      ts: obj.ts || nowISO(),
      level: okLevel,
      msg: obj.msg || "",
      meta: obj.meta || {},
      source: obj.source || "",
    };
  }
  function appendRows(items) {
    setRows(function (prev) {
      var next = prev.slice();
      var i;
      for (i = 0; i < items.length; i++) next.push(items[i]);
      if (next.length > MAX_BUFFER) next.splice(0, next.length - MAX_BUFFER);
      return next;
    });
  }

  // ---------- Filtering (no chained methods) ----------
  var filtered = useMemo(function () {
    var q = search.trim().toLowerCase();
    var hasQ = q.length > 0;
    var out = [];
    var i;
    for (i = 0; i < rows.length; i++) {
      var r = rows[i];
      if (!levels.has(r.level)) continue;
      if (!hasQ) { out.push(r); continue; }
      var hay = (r.ts + " " + r.level + " " + r.msg + " " + JSON.stringify(r.meta) + " " + (r.source || "")).toLowerCase();
      if (hay.indexOf(q) !== -1) out.push(r);
    }
    return out;
  }, [rows, levels, search]);

  // ---------- Virtualization ----------
  var [scrollTop, setScrollTop] = useState(0);
  var rowHeight = dense ? 28 : 36;
  var viewportH = dense ? 520 : 560;
  var totalH = filtered.length * rowHeight;
  var startIndex = Math.max(0, Math.floor(scrollTop / rowHeight) - 10);
  var endIndex = Math.min(filtered.length, Math.ceil((scrollTop + viewportH) / rowHeight) + 10);
  var slice = [];
  var si;
  for (si = startIndex; si < endIndex; si++) {
    slice.push(filtered[si]);
  }
  var padTop = startIndex * rowHeight;
  var padBottom = Math.max(0, totalH - padTop - slice.length * rowHeight);

  useEffect(function () {
    if (!autoScroll) return;
    if (endRef.current) {
      try { endRef.current.scrollIntoView({ behavior: "smooth" }); } catch(_){}
    }
  }, [filtered.length, autoScroll]);

  function handleLevelToggle(lv) {
    setLevels(function (prev) {
      var next = new Set(prev);
      if (next.has(lv)) next.delete(lv); else next.add(lv);
      return next;
    });
  }
  function clearAll() { setRows([]); }
  function copyVisible() {
    var lines = [];
    var i;
    for (i = 0; i < filtered.length; i++) lines.push(lineFormat(filtered[i]));
    var data = lines.join("\n");
    try { navigator.clipboard.writeText(data); } catch(_){}
  }
  function downloadJSON() {
    var blob = new Blob([JSON.stringify(filtered, null, 2)], { type: "application/json" });
    var url = URL.createObjectURL(blob);
    var a = document.createElement("a");
    a.href = url;
    a.download = "server-logs-" + Date.now() + ".json";
    a.click();
    URL.revokeObjectURL(url);
  }
  function lineFormat(r) {
    return "[" + new Date(r.ts).toLocaleString() + "] " + r.level.toUpperCase() + " " + r.msg + (r.source ? (" (" + r.source + ")") : "");
  }

  // ---------- Keyboard shortcuts ----------
  useEffect(function () {
    function onKey(e) {
      if (e && e.target && (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")) return;
      var k = e.key;
      if (k === "p" || k === "P") setPaused(function (v) { return !v; });
      if (k === "c" || k === "C") clearAll();
      if (k === "a" || k === "A") setAutoScroll(function (v) { return !v; });
      if (k === "f" || k === "F") {
        var el = document.getElementById("log-search");
        if (el) el.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    return function () { window.removeEventListener("keydown", onKey); };
  }, []);

  // ---------- UI ----------
  return (
    <div className={classNames(dark ? "dark" : "", "p-4 md:p-6 w-full h-full flex flex-col gap-3 bg-white dark:bg-slate-900")}>
      <header className="sticky top-0 z-10 bg-white/80 dark:bg-slate-900/80 backdrop-blur flex items-center justify-between p-2 rounded-lg border border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-3">
          <h1 className="text-lg md:text-xl font-semibold text-slate-800 dark:text-slate-100">{pageTitle}</h1>
          <span className={classNames(
            "text-xs px-2 py-1 rounded-full",
            connected ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
          )}>
            {connected ? (usingPoll ? "Connected via Poll" : "Connected") : "Disconnected"}
          </span>
          <span className="text-[11px] text-slate-500 dark:text-slate-400">API: {BASE_URL}</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={function(){ setPaused(function(v){return !v;}); }} className="px-3 py-1.5 rounded-md border text-sm hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-600">
            {paused ? "Resume" : "Pause"}
          </button>
          <button onClick={clearAll} className="px-3 py-1.5 rounded-md border text-sm hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-600">Clear</button>
          <button onClick={copyVisible} className="px-3 py-1.5 rounded-md border text-sm hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-600">Copy</button>
          <button onClick={downloadJSON} className="px-3 py-1.5 rounded-md border text-sm hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-600">Download</button>
        </div>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="col-span-2 md:col-span-2 flex items-center gap-2 flex-wrap">
          {LEVELS.map(function(lv){
            return (
              <label key={lv} className="flex items-center gap-1 text-sm">
                <input type="checkbox" checked={levels.has(lv)} onChange={function(){ handleLevelToggle(lv); }} />
                <span className={classNames("px-2 py-0.5 rounded", LEVEL_STYLES[lv])}>{lv}</span>
              </label>
            );
          })}
        </div>
        <div className="col-span-2 flex items-center gap-2">
          <input
            id="log-search"
            value={search}
            onChange={function(e){ setSearch(e.target.value); }}
            placeholder="Search message, meta, source"
            className="w-full border rounded-md px-3 py-2 text-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 border-slate-300 dark:border-slate-600"
          />
        </div>
        <div className="col-span-1 flex items-center justify-end gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
            <input type="checkbox" checked={autoScroll} onChange={function(){ setAutoScroll(function(v){return !v;}); }} />
            Auto scroll
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
            <input type="checkbox" checked={prettyJSON} onChange={function(){ setPrettyJSON(function(v){return !v;}); }} />
            Pretty JSON
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
            <input type="checkbox" checked={dense} onChange={function(){ setDense(function(v){return !v;}); }} />
            Dense
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
            <input type="checkbox" checked={dark} onChange={function(){ setDark(function(v){return !v;}); }} />
            Dark
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
            <input type="checkbox" checked={clientCaptureOn} onChange={function(){ setClientCaptureOn(function(v){return !v;}); }} />
            Client capture
          </label>
        </div>
      </section>

      <section className="border rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm">
        <div
          ref={listRef}
          onScroll={function(e){ setScrollTop(e.currentTarget.scrollTop); }}
          style={{ height: viewportH }}
          className={classNames("overflow-auto font-mono", dense ? "text-[11px]" : "text-xs")}
        >
          <div style={{ paddingTop: padTop, paddingBottom: padBottom }}>
            {slice.map(function(r, idx){
              return <LogRow key={(startIndex + idx) + "-" + r.ts + "-" + (r.msg ? r.msg.slice(0,12) : "")} row={r} pretty={prettyJSON} dense={dense} dark={dark} />
            })}
            <div ref={endRef} />
          </div>
        </div>
      </section>

      <footer className="text-xs text-slate-500 dark:text-slate-400 flex items-center justify-between">
        <div>Showing {filtered.length.toLocaleString()} of {rows.length.toLocaleString()} events</div>
        <div>Shortcuts P pause C clear A autoscroll F find</div>
      </footer>
    </div>
  );
}

function LogRow(props) {
  var row = props.row;
  var pretty = props.pretty;
  var dense = props.dense;
  var dark = props.dark;
  return (
    <div className={classNames("px-3", dense ? "py-1" : "py-1.5", "border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/60 odd:bg-slate-50/40 dark:odd:bg-slate-800/30")}>
      <div className="flex items-center gap-2">
        <span className="text-slate-400 dark:text-slate-500 w-44 shrink-0">{new Date(row.ts).toLocaleString()}</span>
        <span className={classNames("px-2 py-0.5 rounded text-[10px] uppercase tracking-wide", LEVEL_STYLES[row.level])}>{row.level}</span>
        <span className="text-slate-800 dark:text-slate-100">{row.msg}</span>
        {row.source ? (<span className="ml-auto text-slate-400 dark:text-slate-500">{row.source}</span>) : null}
      </div>
      {row.meta && Object.keys(row.meta).length ? (
        <details className="mt-1 group">
          <summary className="cursor-pointer text-[11px] text-slate-500 dark:text-slate-400 select-none">meta</summary>
          <pre className="text-[11px] p-2 bg-slate-50 dark:bg-slate-800 rounded border border-slate-100 dark:border-slate-700 overflow-x-auto">
            {pretty ? JSON.stringify(row.meta, null, 2) : JSON.stringify(row.meta)}
          </pre>
        </details>
      ) : null}
    </div>
  );
}

// ---------- Client observer impl ----------
function attachClientObservers(emit) {
  // console proxy
  var originalConsole = { error: console.error, warn: console.warn, log: console.log };
  console.error = function () {
    try { emit({ ts: nowISO(), level: "error", msg: Array.prototype.join.call(arguments, " "), source: "console" }); } catch(_){ }
    return originalConsole.error.apply(console, arguments);
  };
  console.warn = function () {
    try { emit({ ts: nowISO(), level: "warn", msg: Array.prototype.join.call(arguments, " "), source: "console" }); } catch(_){ }
    return originalConsole.warn.apply(console, arguments);
  };

  // window errors
  function onError(ev) {
    var m = ev && ev.message ? ev.message : "window.onerror";
    emit({ ts: nowISO(), level: "error", msg: m, meta: { filename: ev && ev.filename, lineno: ev && ev.lineno, colno: ev && ev.colno }, source: "client" });
  }
  window.addEventListener("error", onError);

  function onRejection(ev) {
    var m = "Unhandled promise rejection";
    var meta = {};
    try { meta.reason = ev && ev.reason ? (""+ev.reason) : null; } catch(_){}
    emit({ ts: nowISO(), level: "error", msg: m, meta: meta, source: "client" });
  }
  window.addEventListener("unhandledrejection", onRejection);

  // fetch wrapper
  var originalFetch = window.fetch;
  window.fetch = function () {
    var start = Date.now();
    return originalFetch.apply(window, arguments).then(function (res) {
      if (!res.ok) {
        emit({ ts: nowISO(), level: "warn", msg: "Fetch non-OK " + res.status + " " + res.url, meta: { status: res.status, url: res.url, ms: Date.now()-start }, source: "network" });
      }
      return res;
    }).catch(function (err) {
      emit({ ts: nowISO(), level: "error", msg: "Fetch error", meta: { error: ""+err }, source: "network" });
      throw err;
    });
  };

  // XHR wrapper
  var XHR = window.XMLHttpRequest;
  var open = XHR.prototype.open;
  var send = XHR.prototype.send;
  XHR.prototype.open = function (method, url) {
    this.__url = url; this.__method = method; return open.apply(this, arguments);
  };
  XHR.prototype.send = function () {
    var self = this; var start = Date.now();
    function onDone() {
      try {
        if (self.status === 0 || self.status >= 400) {
          emit({ ts: nowISO(), level: self.status >= 500 ? "error" : "warn", msg: "XHR status " + self.status + " " + (self.__url||""), meta: { method: self.__method, url: self.__url, status: self.status, ms: Date.now()-start }, source: "network" });
        }
      } catch(_){}
      self.removeEventListener("loadend", onDone);
    }
    this.addEventListener("loadend", onDone);
    return send.apply(this, arguments);
  };

  // long tasks
  var observer = null;
  try {
    if (window.PerformanceObserver) {
      observer = new PerformanceObserver(function (list) {
        var entries = list.getEntries();
        var i;
        for (i = 0; i < entries.length; i++) {
          var e = entries[i];
          emit({ ts: nowISO(), level: "debug", msg: "Long task " + Math.round(e.duration) + "ms", meta: { name: e.name || "longtask", duration: e.duration }, source: "perf" });
        }
      });
      observer.observe({ entryTypes: ["longtask"] });
    }
  } catch(_){}

  return function teardown() {
    console.error = originalConsole.error;
    console.warn = originalConsole.warn;
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
    if (observer && observer.disconnect) observer.disconnect();
  };
}
